import { Box, Button, Grid, InputLabel, Stack, TextField, Typography } from '@mui/material';
import InputDate from 'components/InputDate';
import AutocompleteCustom, { PropertyNameOptions, SelectTypeOptions } from 'components/Select';
import SelectUser from 'components/SelectUser';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'store/hooks';
import Order from 'types/entities/Order';
import User from 'types/entities/User';
import Scope from 'types/entities/Scope';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { MultiSelectAutoComplete } from 'components/index';
import PurchaseOrderTable from '../PurchaseOrder/PurchaseOrderTable';
import React, { useMemo, useState } from 'react';

import MultiSelectUser from 'components/MultiSelectUser';
import PurchaseOrders from 'types/entities/PurchaseOrders';

type TnMOrderFormProps = {
  handleChange: (e: React.FormEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  handleDateChange: (name: string, date: Moment | null) => void;
  handleSelectOptions: (e: SelectTypeOptions | null, property: PropertyNameOptions) => void;
  handleSelectClient: (client: User | null | undefined) => void;
  handleSwitchSign: (value: boolean) => void;
  handleClientConnectionSwitch: (value: boolean) => void;
  order: Partial<Order> | undefined;
  clients: User[];
  deliveryManagers: User[];
  consultants: User[];
  representatives: User[];
  scopes: Scope[];
  onScopeAdded: (newScope: Scope) => void;
  onScopeArchived: (updatedScope: Scope) => void;
  handleScopesChange: (newSelectedScopes: Scope[]) => void;
  selectedScopes: Scope[];
  onDeliveryManagerArchived: (updatedUser: User) => void;
  onConsultantArchived: (updatedUser: User) => void;
  onRepresentativeArchived: (updatedUser: User) => void;
  handleConsultantChange: (newSelectedConsultants: User[], reason?: string) => void;
  handleDeliveryManagerChange: (newSelectedDeliveryManager: User[], reason?: string) => void;
  handleRepresentativeChange: (newSelectedRepresentative: User[], reason?: string) => void;
  selectedDeliveryManager: User[];
  selectedConsultants: User[];
  selectedRepresentative: User[];
  availableScopes?: Scope[];
  availableDeliveryManagers?: User[];
  availableConsultants?: User[];
  availableRepresentatives?: User[];
  availableClients?: User[];
  onScopeDeletionAttempt?: () => void;
  onDMUserDeletionAttempt?: () => void;
  onConsultantUserDeletionAttempt?: () => void;
  onRepresentativeUserDeletionAttempt?: () => void;
  variant: 'outlined' | 'filled';
};
const TnMOrderForm = (props: TnMOrderFormProps) => {
  const {
    order,
    handleChange,
    handleDateChange,
    handleSelectOptions,
    handleSelectClient,
    handleClientConnectionSwitch,
    clients,
    deliveryManagers,
    consultants,
    representatives,
    scopes,
    onScopeAdded,
    onScopeArchived,
    handleScopesChange,
    selectedScopes,
    onDeliveryManagerArchived,
    onConsultantArchived,
    onRepresentativeArchived,
    handleDeliveryManagerChange,
    handleConsultantChange,
    handleRepresentativeChange,
    selectedDeliveryManager,
    selectedConsultants,
    selectedRepresentative,
    availableScopes,
    availableDeliveryManagers,
    availableConsultants,
    availableRepresentatives,
    onScopeDeletionAttempt = () => undefined,
    onDMUserDeletionAttempt = () => undefined,
    onConsultantUserDeletionAttempt = () => undefined,
    onRepresentativeUserDeletionAttempt = () => undefined,
    variant,
  } = props;

  const [t] = useTranslation();
  const appState = useSelector((state) => state.app);
  const dashboard = useSelector((state) => state.dashboard);

  const resolvedAffair = useMemo(() => {
    if (!appState.affairs?.length) return order?.affair ?? null;

    const idToMatch = order?.affair?.id || order?.affair_id;
    if (!idToMatch) return order?.affair ?? null;

    // 1. Try finding by ID (using loose equality to handle string/number mix)
    let found = appState.affairs.find((a) => String(a.id) === String(idToMatch));
    if (found) return found;

    // 2. Try finding by OTP (as affair_id might be set to the OTP string)
    found = appState.affairs.find((a) => String(a.otp) === String(idToMatch));
    if (found) return found;

    // Last resort: return the provided affair object or null
    return order?.affair ?? null;
  }, [order?.affair, order?.affair_id, appState.affairs]);
  const userLocale = navigator.language || 'fr-FR';
  const dateFormat = userLocale !== 'en-US' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';

  const formatDate = (date: string | number | Date | Moment | null | undefined): string => {
    if (!date) return '';
    // const formattedDate = isEditMode
    //   ? moment.utc(date).format(dateFormat) // For edit mode, keeping UTC formatting
    //   : moment.utc(date).local().format(dateFormat); // For create mode, local time zone
    const formattedDate = moment(date).format('YYYY-MM-DD');
    //  console.log('formattedDate: ', formattedDate);
    return formattedDate;
  };

  return (
    <Box sx={{ mb: 5 }}>
      <Typography sx={{ mb: 3, fontWeight: 'bold' }}>{t('Enter_Professional_Info_Tm')}</Typography>
      <Grid container spacing={4}>
        {appState.affairs && (
          <>
            <Grid item xs={12} lg={6}>
              <AutocompleteCustom
                key={`affair-name-${resolvedAffair?.id ?? order?.affair_id ?? 'empty'}`}
                label={
                  <>
                    {t('Engagement_By_Name_Tm')}{' '}
                    <Typography display="inline" color="error">
                      *
                    </Typography>
                  </>
                }
                options={appState.affairs}
                optionsLabels="name"
                handleSelectOptions={handleSelectOptions}
                selectedOption={resolvedAffair}
                propertyName="affair"
                variant={variant}
                disabled={!!dashboard.todoOrderCreateList?.engagement_name}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <AutocompleteCustom
                key={`affair-otp-${resolvedAffair?.id ?? order?.affair_id ?? 'empty'}`}
                label={
                  <>
                    {t('Engagement_By_Otp_Code_Tm')}{' '}
                    <Typography display="inline" color="error">
                      *
                    </Typography>
                  </>
                }
                options={appState.affairs}
                optionsLabels="otp"
                handleSelectOptions={handleSelectOptions}
                selectedOption={resolvedAffair}
                propertyName="affair"
                variant={variant}
                disabled={!!dashboard.todoOrderCreateList?.otp}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} lg={6}>
          <InputLabel htmlFor="project_name">
            {t('Project_Name_Tm')}{' '}
            <Typography display="inline" color="error">
              *
            </Typography>
          </InputLabel>
          <TextField
            id="project_name_tm"
            name="name_spec"
            value={order?.name_spec || ''}
            onChange={handleChange}
            placeholder={t('Project_Name_Tm')}
            fullWidth
            variant={variant}
            required
          />
        </Grid>

        <Grid item xs={12} lg={3}>
          <InputDate
            label={
              <>
                {t('Start_Date_Tm')}{' '}
                <Typography display="inline" color="error">
                  *
                </Typography>
              </>
            }
            name="start_date"
            id="start_date"
            placeholder={dateFormat}
            onchange={handleDateChange}
            value={formatDate(order?.start_date) || ''}
            required
            variant={variant}
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <InputDate
            label={
              <>
                {t('End_Date_Tm')}{' '}
                <Typography display="inline" color="error">
                  *
                </Typography>
              </>
            }
            name="end_date"
            id="end_date"
            placeholder={dateFormat}
            onchange={handleDateChange}
            value={formatDate(order?.end_date) || ''}
            required
            min={order?.start_date}
            variant={variant}
          />
        </Grid>

        <Grid
          item
          xs={12}
          lg={12}
          sx={{
            p: 1,
            position: 'relative',
            '& .MuiAvatarGroup-root': {
              display: 'flex',
              flexWrap: 'wrap', // Changed from wrap-reverse
              alignItems: 'center',
              justifyContent: 'flex-start', // Changed from flex-end
            },
          }}
        >
          <InputLabel htmlFor="scopes">
            {t('Scopes_Tm')}
            <Typography display="inline" color="error">
              *
            </Typography>
          </InputLabel>{' '}
          <MultiSelectAutoComplete
            items={scopes} // Assuming 'scopes' is an array of { label: string, value: string }
            onChange={handleScopesChange}
            value={selectedScopes} // {selectedScopes} // Assuming you have a state 'selectedScopes' for this
            sx={{ width: '49%' }}
            selectAllLabel="Select scopes on the list or create your own"
            variant={variant}
            onScopeAdded={onScopeAdded} // Pass down the callback
            onScopeArchived={onScopeArchived} // Pass down the callback for archiving scopes
            availableScopes={availableScopes}
            onScopeDeletionAttempt={onScopeDeletionAttempt}
            maximumVisible={4}
          />
        </Grid>
      </Grid>
      <Typography sx={{ mb: 3, fontWeight: 'bold' }}>{t('Stake_Holders_Tm')}</Typography>
      <Grid container spacing={4}>
        <Grid item xs={12} lg={6}>
          <InputLabel htmlFor="clientName">
            {t('Client_Name_Tm')}
            <Typography display="inline" color="error">
              *
            </Typography>
          </InputLabel>
          {clients && (
            <SelectUser
              users={clients}
              selectedUser={order?.client || null}
              onSelectUser={handleSelectClient}
              label=""
              variant={variant}
            />
          )}
        </Grid>
        <Grid
          item
          xs={12}
          lg={6}
          sx={{
            p: 2,
            position: 'relative',
            '& .MuiAvatarGroup-root': {
              display: 'flex',
              flexWrap: 'wrap', // Changed from wrap-reverse
              alignItems: 'center',
              justifyContent: 'flex-start', // Changed from flex-end
            },
          }}
        >
          <InputLabel htmlFor="representative">{t('Representative_Select_Tm')} </InputLabel>
          <MultiSelectUser
            users={representatives}
            id="representative"
            onSelectUsers={handleRepresentativeChange}
            selectedUsers={selectedRepresentative}
            onUserArchived={onRepresentativeArchived}
            label="Select Representative"
            variant={variant}
            availableUsers={availableRepresentatives || []}
            onUserDeletionAttempt={onRepresentativeUserDeletionAttempt}
            maximumVisible={3}
          />
        </Grid>
        <Grid
          item
          xs={12}
          lg={6}
          sx={{
            p: 2,
            position: 'relative',
            '& .MuiAvatarGroup-root': {
              display: 'flex',
              flexWrap: 'wrap', // Changed from wrap-reverse
              alignItems: 'center',
              justifyContent: 'flex-start', // Changed from flex-end
            },
          }}
        >
          <InputLabel htmlFor="deliverymangers">
            {t('Delivery_Managers_Tm')}{' '}
            <Typography display="inline" color="error">
              *
            </Typography>
          </InputLabel>
          <MultiSelectUser
            users={deliveryManagers}
            id="delivery-managers"
            onSelectUsers={handleDeliveryManagerChange}
            selectedUsers={selectedDeliveryManager}
            onUserArchived={onDeliveryManagerArchived}
            label="Select Delivery Managers"
            variant={variant}
            availableUsers={availableDeliveryManagers || []}
            onUserDeletionAttempt={onDMUserDeletionAttempt}
            maximumVisible={3}
          />
        </Grid>

        <Grid
          item
          xs={12}
          lg={6}
          sx={{
            p: 2,
            position: 'relative',
            '& .MuiAvatarGroup-root': {
              display: 'flex',
              flexWrap: 'wrap', // Changed from wrap-reverse
              alignItems: 'center',
              justifyContent: 'flex-start', // Changed from flex-end
            },
          }}
        >
          <InputLabel htmlFor="consultants">
            {t('Consultants_Tm')}
            <Typography display="inline" color="error">
              *
            </Typography>
          </InputLabel>
          <MultiSelectUser
            users={consultants}
            id="consultants"
            onSelectUsers={handleConsultantChange}
            selectedUsers={selectedConsultants}
            onUserArchived={onConsultantArchived}
            label="Select Consultant"
            variant={variant}
            availableUsers={availableConsultants || []}
            onUserDeletionAttempt={onConsultantUserDeletionAttempt}
            maximumVisible={3}
          />
        </Grid>

        <Grid item xs={12} lg={6}>
          <Typography color="neutral" sx={{ mb: 1, fontWeight: 500 }}>
            {t('Client_Connection_Platform')}{' '}
            <Typography display="inline" color="error">
              *
            </Typography>
          </Typography>
          <Stack direction="row" gap={2}>
            <Button
              sx={{ minWidth: 100 }}
              onClick={() => handleClientConnectionSwitch(true)}
              variant={order && order.client_connection ? 'contained' : 'outlined'}
              startIcon={order && order.client_connection ? <CheckCircleIcon /> : null}
            >
              {t('Yes')}
            </Button>
            <Button
              sx={{ minWidth: 100 }}
              onClick={() => handleClientConnectionSwitch(false)}
              variant={order && !order.client_connection ? 'contained' : 'outlined'}
              startIcon={order && !order.client_connection ? <CheckCircleIcon /> : null}
            >
              {t('No')}
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Typography sx={{ mt: 2, mb: 3, fontWeight: 'bold' }}>{t('Purchase_Order')}</Typography>

      <PurchaseOrderTable />
    </Box>
  );
};
export default TnMOrderForm;
